import { memo, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { EnhancedBookCover } from "@/components/ui/enhanced-book-cover";
import { BundleMonthTag } from "@/components/BundleMonthTag";
import { Button } from "@/components/ui/button";
import { Bell, BellRing } from "lucide-react";
import { useAuth } from "@/components/AuthProvider";
import { useBookNotifications } from "@/hooks/use-book-notifications";
import { useSharedElement } from "@/contexts/SharedElementContext";

interface SharedBookCardProps {
  title: string;
  cover_image_url: string;
  rating?: number;
  year?: string;
  onClick?: () => void;
  hideRatings?: boolean;
  bundleStartDate?: string;
  bookId?: number;
  cacheVersion?: number;
  bundleTagType?: 'current' | 'expired' | 'upcoming';
  bundleId?: number;
  bundleTitle?: string | null;
  showNotifyMe?: boolean;
}

export const SharedBookCard = memo(function SharedBookCard({
  title,
  cover_image_url,
  rating,
  year,
  onClick,
  hideRatings = false,
  bundleStartDate,
  bookId,
  cacheVersion,
  bundleTagType,
  bundleId,
  bundleTitle,
  showNotifyMe = false
}: SharedBookCardProps) {
  const { user } = useAuth();
  const { isSubscribed, subscribeToBook, unsubscribeFromBook } = useBookNotifications();
  const { registerElement, openElement } = useSharedElement();
  const cardRef = useRef<HTMLDivElement>(null);
  
  const elementId = `book-${bookId}`;

  useEffect(() => {
    if (cardRef.current && bookId) {
      const rect = cardRef.current.getBoundingClientRect();
      registerElement(elementId, rect);
    }
  }, [elementId, registerElement, bookId]);

  const handleNotifyClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!bookId) return;
    
    if (isSubscribed(bookId)) {
      await unsubscribeFromBook(bookId);
    } else {
      await subscribeToBook(bookId, bundleId);
    }
  };

  const handleClick = () => {
    if (bookId) {
      openElement(elementId);
    }
    onClick?.();
  };

  return (
    <motion.div
      ref={cardRef}
      layoutId={elementId}
      className="group cursor-pointer"
      onClick={handleClick}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      transition={{ type: "spring", stiffness: 400, damping: 30 }}
    >
      <div className="relative aspect-[2/3] rounded-[8px] overflow-hidden shadow-lg transition-all duration-300 border-2 border-transparent lg:hover:border-white">
        <EnhancedBookCover
          src={cover_image_url}
          alt={title}
          className="w-full h-full"
          bookId={bookId}
          cacheVersion={cacheVersion}
          showSkeleton={true}
        />
        
        {/* Bundle Tag */}
        {bundleTagType && bundleStartDate && (
          <BundleMonthTag 
            startDate={bundleStartDate} 
            tagType={bundleTagType}
            bundleTitle={bundleTitle}
          />
        )}
        
        {/* Rating overlay */}
        {!hideRatings && rating && (
          <div className="absolute bottom-2 right-2 bg-black/70 backdrop-blur-sm rounded-lg px-2 py-1">
            <div className="flex items-center gap-1">
              <svg className="w-3 h-3 text-yellow-400 fill-current" viewBox="0 0 20 20">
                <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z"/>
              </svg>
              <span className="text-white text-xs font-medium">
                {rating.toFixed(1)}
              </span>
            </div>
          </div>
        )}
        
        {/* Year badge */}
        {year && (
          <div className="absolute top-2 right-2 bg-black/70 backdrop-blur-sm rounded-lg px-2 py-1">
            <span className="text-white text-xs font-medium">{year}</span>
          </div>
        )}
        
        {/* Notify Me button for upcoming books */}
        {showNotifyMe && user && bookId && (
          <div className="absolute bottom-2 left-2">
            <Button
              onClick={handleNotifyClick}
              size="sm"
              variant={isSubscribed(bookId) ? "default" : "outline"}
              className={`text-xs px-2 py-1 h-auto backdrop-blur-sm transition-all ${
                isSubscribed(bookId) 
                  ? "bg-primary/90 hover:bg-primary text-white border-primary/50" 
                  : "bg-white/10 hover:bg-white/20 text-white border-white/30"
              }`}
            >
              {isSubscribed(bookId) ? (
                <>
                  <BellRing className="w-3 h-3 mr-1" />
                  Notified
                </>
              ) : (
                <>
                  <Bell className="w-3 h-3 mr-1" />
                  Notify Me
                </>
              )}
            </Button>
          </div>
        )}
        
        {/* Coming Soon overlay for upcoming books without access */}
        {showNotifyMe && (
          <div className="absolute inset-0 bg-black/60 backdrop-blur-[1px] rounded-[8px] flex items-center justify-center">
            <div className="text-white text-center">
              <div className="text-sm font-semibold mb-1">Coming Soon</div>
              {!user && (
                <div className="text-xs opacity-80">Sign in to get notified</div>
              )}
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
});
